import {
    ShieldCheck,
    ClipboardCheck,
    FileText,
    Wallet,
    TrendingUp,
    BadgeCheck,
    BarChart3,
} from "lucide-react";

export const IMAGES = {
    hero: "/hero-team-real.jpg",
    trust: "https://images.unsplash.com/photo-1674775372058-c4c8813c6611?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxzbWlsaW5nJTIwcHJvZmVzc2lvbmFsJTIwZGVudGlzdHxlbnwwfHx8fDE3ODYxMzA4Njh8MA&ixlib=rb-4.1.0&q=85",
    services:
        "https://images.unsplash.com/photo-1704455306251-b4634215d98f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxkZW50YWwlMjBjbGluaWMlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODYxMzA4Njh8MA&ixlib=rb-4.1.0&q=85",
    reception:
        "https://images.unsplash.com/photo-1762625570087-6d98fca29531?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBkZW50YWwlMjBvZmZpY2UlMjByZWNlcHRpb258ZW58MHx8fHwxNzg2MTMyNzU5fDA&ixlib=rb-4.1.0&q=85",
    analytics:
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxmaW5hbmNpYWwlMjBjaGFydHMlMjBhbmFseXRpY3MlMjBkYXNoYm9hcmR8ZW58MHx8fHwxNzgyOTM4OTc0fDA&ixlib=rb-4.1.0&q=85",
};

export const HEADLINE = "Your Dental Practice's Back Office, Handled.";
export const SUBHEADLINE =
    "DentAssists handles insurance verification, billing, payment posting, AR follow-up, credentialing, and reporting for U.S. dental practices — working directly inside the software you already use, so your team doesn't have to learn anything new.";

export const TRUST_POINTS = [
    "HIPAA-compliant process, backed by a signed BAA on every engagement",
    "Works inside Dentrix, Eaglesoft, Open Dental, and more",
    "U.S.-based specialists",
];

export const PM_SYSTEMS = ["Dentrix", "Eaglesoft", "Open Dental", "Curve", "Denticon", "Other"];

export const STATS = [
    { value: "50+", label: "Practices served" },
    { value: "8", label: "Years in business" },
    { value: "35%", label: "Avg. AR reduction (90 days)" },
    { value: "Same-day", label: "Verification turnaround" },
];

export const SERVICES = [
    {
        icon: ClipboardCheck,
        title: "Insurance Verification",
        blurb:
            "Coverage confirmed before the patient sits in the chair. We check eligibility and benefits days ahead of each appointment, so your front desk isn't scrambling and your patients aren't surprised by their bill.",
    },
    {
        icon: FileText,
        title: "Billing & Claims Submission",
        blurb:
            "Clean claims, submitted fast. We code, scrub, and submit accurately the first time — fewer denials, faster payment.",
    },
    {
        icon: Wallet,
        title: "Payment Posting",
        blurb:
            "Every payment matched and posted — EFT, ERA, check, or card — so your ledger reflects what actually happened.",
    },
    {
        icon: TrendingUp,
        title: "AR & Aging Management",
        blurb:
            "Your aging report, actually worked. Open claims get followed up, appealed, or resolved instead of sitting there.",
    },
    {
        icon: BadgeCheck,
        title: "Credentialing & Enrollment",
        blurb:
            "Get on payer panels faster. We handle new-provider credentialing and re-credentialing so you're not the one on hold with insurance companies.",
    },
    {
        icon: BarChart3,
        title: "Reporting & Analytics",
        blurb:
            "Know your numbers without pulling them yourself — regular reports on collections, aging, and claim status, in plain language.",
    },
];

export const STEPS = [
    {
        n: "01",
        title: "Free consultation",
        blurb: "Tell us about your practice and current billing setup.",
    },
    {
        n: "02",
        title: "Practice assessment",
        blurb: "We review your PM system, payer mix, and current AR.",
    },
    {
        n: "03",
        title: "Onboarding & BAA",
        blurb: "We sign a Business Associate Agreement and set up secure access.",
    },
    {
        n: "04",
        title: "Go live",
        blurb:
            "We start working claims inside your existing system, typically within 5 days.",
    },
];

export const WHY_POINTS = [
    "Dental-only focus, not a generalist medical billing shop",
    "We work inside your existing practice management software — nothing new for your staff to learn",
    "8 years of dental billing experience · 50+ practices served",
    "HIPAA-compliant with a signed BAA before we ever touch a claim",
];

export const RESULTS = [
    { stat: "35%", line: "Reduced AR over 90 days", meta: "Across practices served" },
    { stat: "10% → 2%", line: "Cut claim denial rate", meta: "Across practices served" },
    { stat: "Same-day", line: "Verification turnaround", meta: "Standard on every engagement" },
];

export const PROBLEMS = [
    "High claim denial and rejection rates",
    "Aging AR that never actually gets worked",
    "Front desk buried in insurance verification calls",
    "Missed eligibility checks before appointments",
    "Credentialing delays that stall new providers",
    "Coding errors that trigger avoidable rejections",
];

export const BENEFITS = [
    "Dedicated, dental-only billing specialists",
    "We work inside Dentrix, Eaglesoft & Open Dental",
    "HIPAA-compliant with a signed BAA on every engagement",
    "Plain-language monthly reporting on collections & AR",
    "Same-day insurance verification turnaround",
    "Faster reimbursements with fewer claim denials",
];

// NOTE: Placeholder testimonials — replace with real, approved client quotes before launch.
export const TESTIMONIALS = [
    {
        quote: "Our AR dropped fast and the front desk finally stopped living on hold with insurance companies. It felt like adding a whole billing department overnight.",
        name: "Office Manager",
        role: "Family Dental Practice · Houston, TX",
        initials: "HD",
    },
    {
        quote: "Verification is done before patients even walk in, so there are no billing surprises. Denials are down and payments come in noticeably faster.",
        name: "Practice Owner",
        role: "General Dentistry · Austin, TX",
        initials: "AD",
    },
    {
        quote: "They work right inside Open Dental, so nothing changed for my team. The monthly reports finally make our numbers easy to understand.",
        name: "Lead Dentist",
        role: "Multi-location Group · Dallas, TX",
        initials: "DG",
    },
];

export const FAQS = [
    {
        q: "Is this HIPAA-compliant?",
        a: "Yes. We sign a Business Associate Agreement with every practice before we access any patient or claims data, and our team is trained on HIPAA security requirements.",
    },
    {
        q: "Do we need new software?",
        a: "No. We work directly inside your existing practice management system — Dentrix, Eaglesoft, Open Dental, and others.",
    },
    {
        q: "How long does onboarding take?",
        a: "5 days from signed agreement to go-live, depending on your current AR backlog.",
    },
    {
        q: "Can we outsource just one service instead of everything?",
        a: "Yes — verification only, billing only, or the full package. We package services to match how your practice actually works.",
    },
];

export const TRUST_ICON = ShieldCheck;

export const CONTACT_EMAIL = "Info@dentassists.com";

export const FAQ_CATEGORIES = [
    {
        category: "General",
        items: [
            {
                q: "What does DentAssists actually do?",
                a: "We handle the back-office side of running a dental practice — insurance verification, billing and claims submission, payment posting, AR and aging follow-up, credentialing, and reporting — so your team can focus on patients instead of paperwork.",
            },
            {
                q: "What kinds of practices do you work with?",
                a: "[Solo practices, group practices, multi-location DSOs — specify which you actually serve.] General dentistry and [specialty practices you support — orthodontics, oral surgery, periodontics, endodontics, etc.].",
            },
            {
                q: "How is this different from hiring an in-house biller?",
                a: "An in-house hire means one person covering vacations, sick days, and turnover risk alone. With DentAssists, [a dedicated point of contact backed by a full team] covers your account, so coverage doesn't disappear when one person is out.",
            },
            {
                q: "Will this replace my front desk or office manager?",
                a: "No. We take the insurance and billing workload off their plate so they can spend more time with patients and less time on hold with payers — your team stays, just with a lighter administrative load.",
            },
            {
                q: "Do we have to use all six services, or can we start with just one?",
                a: "[Yes — start with a single service, like insurance verification or billing only, and add more later.] Edit to match how you actually package services.",
            },
        ],
    },
    {
        category: "Security & Compliance",
        items: [
            {
                q: "Is DentAssists HIPAA-compliant?",
                a: "Yes. We sign a Business Associate Agreement (BAA) with every practice before we access any patient or claims data, and our team is trained on HIPAA security requirements.",
            },
            {
                q: "Will you sign our BAA, or do we sign yours?",
                a: "[Either — we can work from your practice's BAA template or provide our own.] Specify your actual process.",
            },
            {
                q: "How do you access our data — do we need to install anything?",
                a: "No new software. We work directly inside the practice management system you already use. Nothing about your data moves to a separate platform.",
            },
            {
                q: "Who at DentAssists can actually see our patient data?",
                a: "[Only the specific team members assigned to your account, under role-based access controls.] Specify your real internal access policy.",
            },
            {
                q: "What happens to our data if we stop working together?",
                a: "[Access is revoked immediately on termination, and we retain no copies of your practice data outside your own system.] Confirm this matches your actual offboarding process.",
            },
        ],
    },
    {
        category: "Practice Management Systems",
        items: [
            {
                q: "Which practice management systems do you support?",
                a: "Dentrix, Eaglesoft, Open Dental, Denticon, CareStack, Curve, and others. If yours isn't listed, ask — [we're often able to support systems not listed here / specify honestly if there are real limits].",
            },
            {
                q: "We're switching practice management systems soon — can you still help?",
                a: "[Yes — let us know during onboarding and we'll adjust to your new system.] Confirm this is accurate before publishing.",
            },
        ],
    },
    {
        category: "Pricing & Contracts",
        items: [
            {
                q: "How much does this cost?",
                a: "We price as a percentage of collections or a flat monthly fee per provider. Every quote is based on your practice's size and claim volume — request a consultation for exact numbers.",
            },
            { q: "Are there setup fees?", a: "[Yes/No — specify.]" },
            {
                q: "Is there a contract or minimum commitment?",
                a: "[No long-term contract, month-to-month. / X-month minimum. — specify your actual terms.]",
            },
            {
                q: "Can we cancel at any time?",
                a: "[Yes, with X days' written notice.] Specify your actual cancellation policy.",
            },
        ],
    },
    {
        category: "Onboarding & Support",
        items: [
            {
                q: "How long does onboarding take?",
                a: "Typically 5 days from signed agreement to going live, depending on your current AR backlog and practice management system.",
            },
            {
                q: "Who do we talk to day-to-day?",
                a: "[A dedicated point of contact who knows your account, backed by our full team.] Specify your real support model.",
            },
            {
                q: "What are your support hours, and how fast do you respond?",
                a: "[Specify hours/days and typical response time.]",
            },
            {
                q: "Do you work with multi-location practices or DSOs?",
                a: "[Yes — specify how pricing or process differs for multi-location groups, if it does.]",
            },
        ],
    },
];
